import React, { useEffect, useMemo, useState } from 'react';

import { listSurgeries } from '../../services/api/surgeryApi';
import { listProceduresForDept } from '../../services/api/procedureApi';
import { INTERVENTION_TYPES } from '../../constants/admission';
import { formatRupees } from './estimateModel';

const ITEMS_PER_GROUP = 8;

/**
 * Client-side DISPLAY snapshot of a catalog item → a planned-intervention line.
 * The SERVER re-snapshots price/name/gst authoritatively at submit (client sends
 * only {type, catalog_ref}); this is the estimate-rail preview only.
 *   surgery  → package_price when billing_mode=PACKAGE, else Σ component prices.
 *   procedure→ default_cost.
 */
export function snapshotIntervention(type, item) {
  if (type === INTERVENTION_TYPES.SURGERY) {
    const packagePrice = Number(item.package_price) || 0;
    const itemized = Array.isArray(item.components)
      ? item.components.reduce((s, c) => s + (Number(c && c.price) || 0), 0)
      : 0;
    const billing_mode = item.billing_mode || 'PACKAGE';
    return {
      type,
      catalog_ref: item.surgery_id,
      name_snapshot: item.name,
      price_snapshot: billing_mode === 'PACKAGE' ? packagePrice : itemized,
      gst_snapshot: Number(item.gst_rate) || 0,
      billing_mode,
    };
  }
  return {
    type,
    catalog_ref: item.procedure_id,
    name_snapshot: item.name,
    price_snapshot: Number(item.default_cost) || 0,
    gst_snapshot: Number(item.gst_rate) || 0,
    billing_mode: null,
  };
}

/**
 * PlannedInterventionsPicker (S9) — ELECTIVE-only picker of planned surgeries +
 * procedures. Anchors on the ADMISSION's department with the SAME rule-(a)
 * never-exclude union as the ward picker: items tagged to the anchor group first
 * ("this admission's department"), everything else under "All other departments"
 * and never removed. Segmented catalog tabs (surgery / procedure), search, "+n
 * more" per group. Duplicate lines ALLOWED (each Add → a new line).
 *
 * The parent OWNS the selected list (`value`) and the estimate; this component
 * calls `onAdd(line)`. Hidden entirely by the parent on emergency source.
 *
 * Data fetch: one call per catalog tab (bounded; a tab keeps its fetched list).
 */
export default function PlannedInterventionsPicker({ anchorDeptId, onAdd }) {
  const [catalog, setCatalog] = useState(INTERVENTION_TYPES.SURGERY);
  const [surgeries, setSurgeries] = useState(null);
  const [procedures, setProcedures] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  // Fetch each catalog ONCE (bounded); the anchor scopes the union server-side.
  useEffect(() => {
    let alive = true;
    if (catalog === INTERVENTION_TYPES.SURGERY && surgeries === null) {
      listSurgeries({ department_id: anchorDeptId || undefined }).then((res) => {
        if (alive && res.success) setSurgeries(res.data.surgeries);
        else if (alive) setSurgeries([]);
      });
    }
    if (catalog === INTERVENTION_TYPES.PROCEDURE && procedures === null) {
      listProceduresForDept({ department_id: anchorDeptId || undefined }).then((res) => {
        if (alive && res.success) setProcedures(res.data.procedures);
        else if (alive) setProcedures([]);
      });
    }
    return () => { alive = false; };
  }, [catalog, anchorDeptId, surgeries, procedures]);

  const items = catalog === INTERVENTION_TYPES.SURGERY ? surgeries : procedures;

  const { anchorItems, otherItems } = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = search.trim().toLowerCase();
    const match = (it) => !q || (it.name || '').toLowerCase().includes(q);
    const anchor = [];
    const other = [];
    for (const it of list) {
      if (!match(it)) continue;
      const inAnchor = anchorDeptId && (it.department_ids || []).includes(anchorDeptId);
      (inAnchor ? anchor : other).push(it);
    }
    return { anchorItems: anchor, otherItems: other };
  }, [items, anchorDeptId, search]);

  const priceOf = (it) => snapshotIntervention(catalog, it).price_snapshot;

  const renderGroup = (label, groupItems, key) => {
    if (groupItems.length === 0) return null;
    const isOpen = expanded[key];
    const shown = isOpen ? groupItems : groupItems.slice(0, ITEMS_PER_GROUP);
    const rest = groupItems.length - shown.length;
    return (
      <div className="mb-3" key={key}>
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
          {label} ({groupItems.length})
        </div>
        <div className="flex flex-wrap gap-2">
          {shown.map((it) => {
            const id = it.surgery_id || it.procedure_id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onAdd(snapshotIntervention(catalog, it))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] hover:border-teal-500 flex items-center gap-1.5"
              >
                {it.name}
                <span className="text-[10.5px] text-slate-500 dark:text-slate-400">
                  {formatRupees(priceOf(it))}
                  {catalog === INTERVENTION_TYPES.SURGERY && it.billing_mode === 'PACKAGE' ? ' pkg' : ''}
                </span>
              </button>
            );
          })}
          {rest > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((s) => ({ ...s, [key]: true }))}
              className="px-3 py-1.5 rounded-lg border border-dashed border-slate-400 dark:border-slate-500 bg-slate-50 dark:bg-slate-800 text-teal-700 dark:text-teal-300 text-[12.5px] font-semibold"
            >
              +{rest} more
            </button>
          )}
        </div>
      </div>
    );
  };

  const segBtn = (cat, label) => (
    <button
      type="button"
      onClick={() => { setCatalog(cat); setSearch(''); }}
      className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium border ${
        catalog === cat
          ? 'bg-teal-600 border-teal-600 text-white'
          : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="planned-interventions-picker">
      <div className="inline-flex gap-1.5 p-1 rounded-lg bg-slate-100 dark:bg-slate-800 mb-2.5">
        {segBtn(INTERVENTION_TYPES.SURGERY, 'Surgeries')}
        {segBtn(INTERVENTION_TYPES.PROCEDURE, 'Procedures')}
      </div>
      <input
        className="w-full mb-3 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        placeholder={catalog === INTERVENTION_TYPES.SURGERY ? 'Search surgeries…' : 'Search procedures…'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search catalog"
      />
      {items === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <>
          {renderGroup("This admission's department", anchorItems, `${catalog}-anchor`)}
          {renderGroup('All other departments', otherItems, `${catalog}-other`)}
          {anchorItems.length === 0 && otherItems.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Nothing matches your search.</p>
          )}
        </>
      )}
    </div>
  );
}
